import type { GalleryImage } from '../content/types';

/**
 * 03 · Pantalla completa con PhotoSwipe 5 (zoom con pellizco, doble toque y — si el táctil se
 * reporta como mouse, sin multitáctil — zoom con un toque). La UI propia replica el diseño:
 * cabecera con nombre, imagen en la franja 240–1200 px, y debajo flechas, contador, puntos y
 * "Cerrar". Se carga diferido, pero se precarga en reposo para que abrir sea inmediato.
 */
type PSModule = typeof import('photoswipe');
let modP: Promise<PSModule> | null = null;

export function preloadViewer(): Promise<PSModule> {
  if (!modP) {
    modP = Promise.all([import('photoswipe'), import('photoswipe/style.css'), import('./viewer.css')]).then(([m]) => m);
    modP.catch(() => (modP = null));
  }
  return modP;
}

export interface ViewerHandle {
  close: () => void;
}

export interface ViewerOptions {
  items: GalleryImage[];
  index: number;
  src: (f: GalleryImage['full']) => string;
  title: string;
  tabLabel: string;
  label: (img: GalleryImage | undefined, i: number) => string;
  onChange: (i: number) => void;
  onClose: () => void;
}

const STAGE_W = 1080;
const STAGE_H = 1920;
const PAD_TOP = 240;
const PAD_BOTTOM = STAGE_H - 240 - 960;

export async function openViewer(o: ViewerOptions): Promise<ViewerHandle> {
  const { default: PhotoSwipe } = await preloadViewer();
  const scale = () => Math.min(window.innerWidth / STAGE_W, window.innerHeight / STAGE_H);

  const pswp = new PhotoSwipe({
    dataSource: o.items.map((it) => ({
      src: o.src(it.full),
      // Placeholder inmediato: la variante principal ya está decodificada en la galería
      msrc: o.src(it.main) || it.lqip,
      width: it.fullW,
      height: it.fullH,
      alt: it.caption || '',
    })),
    index: o.index,
    bgOpacity: 1,
    loop: true,
    preload: [1, 1],
    wheelToZoom: true,
    showHideAnimationType: 'fade',
    showAnimationDuration: 200,
    hideAnimationDuration: 200,
    imageClickAction: 'zoom',
    tapAction: 'zoom',
    doubleTapAction: 'zoom',
    bgClickAction: () => {},
    closeOnVerticalDrag: true,
    pinchToClose: false,
    secondaryZoomLevel: 2,
    maxZoomLevel: 4,
    zoom: false,
    close: false,
    arrowPrev: false,
    arrowNext: false,
    counter: false,
    clickToCloseNonZoomable: false,
    errorMsg: 'La imagen no está disponible',
    mainClass: 'pswp--totem',
    paddingFn: () => {
      const s = scale();
      const offY = (window.innerHeight - STAGE_H * s) / 2;
      const offX = (window.innerWidth - STAGE_W * s) / 2;
      return { top: offY + PAD_TOP * s, bottom: offY + PAD_BOTTOM * s, left: offX, right: offX };
    },
  });

  pswp.on('uiRegister', () => {
    pswp.ui?.registerElement({
      name: 'totem-ui',
      appendTo: 'root',
      order: 30,
      isButton: false,
      onInit: (el) => buildUi(el, pswp, o, scale),
    });
  });
  pswp.on('change', () => o.onChange(pswp.currIndex));
  pswp.on('destroy', () => o.onClose());
  pswp.init();
  return { close: () => pswp.close() };
}

function buildUi(el: HTMLElement, pswp: InstanceType<PSModule['default']>, o: ViewerOptions, scale: () => number) {
  el.className = 'totem-pswp-ui';
  el.innerHTML = `
    <div class="tv-stage">
      <div class="tv-head">
        <div class="tv-title"></div>
        <div class="tv-sub"></div>
      </div>
      <div class="tv-bottom">
        <div class="tv-row">
          <button type="button" class="tv-btn tv-prev" aria-label="Anterior">${arrow('left')}</button>
          <div class="tv-count"></div>
          <button type="button" class="tv-btn tv-next" aria-label="Siguiente">${arrow('right')}</button>
        </div>
        <div class="tv-dots"></div>
        <div class="tv-hint">Toca la imagen para acercar · arrastra para recorrerla</div>
        <button type="button" class="tv-close">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/></svg>
          <span>Cerrar</span>
        </button>
      </div>
    </div>`;
  const $ = <T extends HTMLElement>(s: string) => el.querySelector(s) as T;
  const stage = $('.tv-stage');
  const title = $('.tv-title');
  const sub = $('.tv-sub');
  const count = $('.tv-count');
  const dots = $('.tv-dots');
  const n = o.items.length;
  title.textContent = o.title;
  dots.innerHTML = n <= 20 ? o.items.map(() => '<i></i>').join('') : '';
  if (n < 2) {
    $('.tv-prev').style.visibility = 'hidden';
    $('.tv-next').style.visibility = 'hidden';
  }

  const fit = () => {
    const s = scale();
    stage.style.transform = `translate(${(window.innerWidth - STAGE_W * s) / 2}px, ${(window.innerHeight - STAGE_H * s) / 2}px) scale(${s})`;
  };
  const sync = () => {
    const i = pswp.currIndex;
    sub.textContent = `${o.tabLabel} · ${o.label(o.items[i], i)}`;
    count.textContent = `${i + 1} / ${n}`;
    Array.from(dots.children).forEach((d, k) => d.classList.toggle('on', k === i));
  };
  // Con zoom, la imagen ocupa toda la pantalla: se ocultan textos y quedan solo los controles
  const zoomState = () => {
    const sl = pswp.currSlide;
    el.classList.toggle('tv-zoomed', !!sl && sl.currZoomLevel > sl.zoomLevels.initial + 0.01);
  };
  fit();
  sync();
  pswp.on('change', sync);
  pswp.on('zoomPanUpdate', zoomState);
  pswp.on('change', zoomState);
  pswp.on('resize', fit);

  const stop = (e: Event) => e.stopPropagation();
  for (const b of Array.from(el.querySelectorAll('button'))) {
    b.addEventListener('pointerdown', stop);
  }
  $('.tv-prev').addEventListener('click', () => pswp.prev());
  $('.tv-next').addEventListener('click', () => pswp.next());
  $('.tv-close').addEventListener('click', () => pswp.close());
}

function arrow(dir: 'left' | 'right') {
  const d = dir === 'right' ? 'M5 12h13M13 6l6 6-6 6' : 'M19 12H6M11 6l-6 6 6 6';
  return `<svg width="44" height="44" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="${d}" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

/**
 * Visor para la versión de asesores (laptop/móvil): PhotoSwipe con su UI estándar
 * (flechas, zoom, contador, cerrar) y teclado.
 */
export async function openSimpleViewer(o: {
  items: GalleryImage[];
  index: number;
  src: (f: GalleryImage['full']) => string;
  onChange: (i: number) => void;
  onClose: () => void;
  /** Elemento que muestra la imagen actual (para animar el zoom desde ahí) */
  fromEl?: () => HTMLElement | null;
}): Promise<ViewerHandle> {
  const { default: PhotoSwipe } = await preloadViewer();
  const pswp = new PhotoSwipe({
    // El placeholder es la MISMA imagen que ya se ve en pantalla: sin saltos ni recortes al abrir
    dataSource: o.items.map((it) => ({ src: o.src(it.full), msrc: o.src(it.main) || o.src(it.full), width: it.fullW, height: it.fullH, alt: it.caption || '' })),
    index: o.index,
    bgOpacity: 0.97,
    loop: true,
    wheelToZoom: true,
    showHideAnimationType: o.fromEl ? 'zoom' : 'fade',
    showAnimationDuration: 280,
    hideAnimationDuration: 240,
    easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)',
    padding: { top: 48, bottom: 48, left: 16, right: 16 },
    mainClass: 'pswp--asesor',
    errorMsg: 'La imagen no está disponible',
    closeTitle: 'Cerrar (Esc)',
    zoomTitle: 'Acercar',
    arrowPrevTitle: 'Anterior',
    arrowNextTitle: 'Siguiente',
  });
  // Rectángulo real de la imagen dentro del recuadro (object-fit: contain): el zoom crece desde ahí
  pswp.addFilter('thumbBounds', (bounds, _data, index) => {
    const el = o.fromEl?.();
    const it = o.items[index];
    if (!el || !it) return bounds as { x: number; y: number; w: number };
    const r = el.getBoundingClientRect();
    const k = it.fullW / it.fullH;
    if (r.width / r.height > k) {
      const w = r.height * k;
      return { x: r.left + (r.width - w) / 2, y: r.top, w };
    }
    return { x: r.left, y: r.top + (r.height - r.width / k) / 2, w: r.width };
  });
  pswp.on('change', () => o.onChange(pswp.currIndex));
  pswp.on('destroy', () => o.onClose());
  pswp.init();
  return { close: () => pswp.close() };
}
